import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { validateVariantSelection } from './variant-selection-validation-v3.js';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  /**
   * Swym のお気に入りボタンは2系統の markup がある。
   * - `#swym-atw-button-container` … Advanced Wishlist の PDP ボタン（STG）
   * - `.swym-wishlist-button-bar`  … 従来の Wishlist Plus ボタン（PRD）
   * どちらが注入されるかはストアのアプリ設定で変わるため両方を対象にする。
   */
  static WISH_SELECTOR = '#swym-atw-button-container, .swym-wishlist-button-bar';

  /**
   * 実際にボタンを内包している方のコンテナを返す。
   * STG では Advanced Wishlist の `#swym-atw-button-container`（実体）と、Wishlist Plus の
   * 空の `.swym-wishlist-button-bar`（プレースホルダのみ・中身なし）が両方 DOM に出る。
   * `querySelector` は DOM 順で拾うので空の方を掴んでしまい、バーのお気に入りが消える。
   * @returns {Element | null}
   */
  static #findWishButton() {
    const candidates = [...document.querySelectorAll(StickyAddToCartComponent.WISH_SELECTOR)];
    return candidates.find((el) => el.querySelector('button, a')) ?? null;
  }

  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {MutationObserver | null} */
  #slotObserver = null;

  /** @type {number | null} */
  #slotFrame = null;

  /** @type {MediaQueryList | null} */
  #mobileQuery = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // #252: tu-hacci はバーを常時表示にするため、表示制御の IntersectionObserver は張らない。
    if (this.dataset.alwaysVisible === 'true') {
      this.#showStickyBar();
      this.#setupSlotObserver(signal);
      // #266: 常時表示モードでは #setupIntersectionObserver を通らないので、
      // 代理クリック先のボタンをここで拾っておく（クリック時にも引き直す）。
      this.#resolveTargetAddToCartButton();
    } else {
      this.#setupIntersectionObserver();
    }

    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#slotObserver?.disconnect();
    if (this.#slotFrame) cancelAnimationFrame(this.#slotFrame);
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * #252: Swym のお気に入りボタンはアプリが後から DOM に差し込むため、
   * 出現を監視して追従バーのスロットへ移設する。
   * バリエーション切替で再注入されることがあるので監視は張り続ける。
   *
   * 再入荷通知はバー内に代理ボタンを静的に描いてあるので移設しない（`pdp-bis`）。
   */
  #setupSlotObserver(signal) {
    // #266: デスクトップではページ内の購入エリアにお気に入りを残すため、
    // ブレークポイントを跨いだら配置をやり直す。
    this.#mobileQuery = window.matchMedia('(max-width: 749px)');
    this.#mobileQuery.addEventListener('change', () => this.#adoptExternalControls(), { signal });

    this.#adoptExternalControls();

    this.#slotObserver = new MutationObserver(() => {
      if (this.#slotFrame) return;
      this.#slotFrame = requestAnimationFrame(() => {
        this.#slotFrame = null;
        this.#adoptExternalControls();
      });
    });

    this.#slotObserver.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Swym お気に入りボタンを置き場所へ移す。
   * SP は追従バーのスロット、PC は購入エリアの `[data-wish-home]`（#266）。
   *
   * Swym の注入先はカートボタンの有無で変わり、在庫切れでカートボタンを畳むと
   * form 直下へ落ちる（テーマの上書き CSS のスコープ外＝Swym 既定の見た目になる）。
   * どこへ注入されても必ず置き場所へ引き取る。
   */
  #adoptExternalControls() {
    const { wishSlot } = this.refs;
    if (!wishSlot) return;

    const wish = StickyAddToCartComponent.#findWishButton();
    if (!wish) return;

    if (this.#isMobileViewport()) {
      // 空のダミーが先に入っていると `:empty` が効かず 0 幅の枠が残るので入れ替える
      if (!wishSlot.contains(wish)) wishSlot.replaceChildren(wish);
    } else {
      this.#restoreWishHome(wish);
    }
  }

  /**
   * morph 前に、バー内へ移設した外部ノードを退避する
   * @returns {Element[]}
   */
  #detachExternalControls() {
    const nodes = [this.querySelector(StickyAddToCartComponent.WISH_SELECTOR)].filter((node) => node != null);

    for (const node of nodes) this.#getHolder().appendChild(node);

    return /** @type {Element[]} */ (nodes);
  }

  /**
   * morph 後に退避したノードをスロットへ戻す
   * @param {Element[]} nodes
   */
  #reattachExternalControls(nodes) {
    if (nodes.length === 0) return;
    this.#adoptExternalControls();
  }

  /**
   * @returns {boolean}
   */
  #isMobileViewport() {
    return this.#mobileQuery?.matches ?? window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * お気に入りボタンを PC の置き場所（購入エリア内）へ移す。
   * この関数は DOM 変化のたびに呼ばれるので、既に収まっているときは何もしない
   * （MutationObserver と往復して無限ループになる）。
   * @param {Element} wish
   */
  #restoreWishHome(wish) {
    const home = document.querySelector('.product-information [data-wish-home]');
    if (home) {
      if (home.firstElementChild !== wish) home.replaceChildren(wish);
      return;
    }

    // 置き場所が無いテンプレート（旧 markup 等）では購入エリアの直後に置く
    const buyButtons = document.querySelector('.product-information .buy-buttons-block');
    if (buyButtons && wish.previousElementSibling !== buyButtons) {
      buyButtons.insertAdjacentElement('afterend', /** @type {HTMLElement} */ (wish));
    }
  }

  /**
   * 退避用の非表示ホルダー
   * @returns {HTMLElement}
   */
  #getHolder() {
    let holder = document.getElementById('sticky-add-to-cart-holder');
    if (!holder) {
      holder = document.createElement('div');
      holder.id = 'sticky-add-to-cart-holder';
      holder.hidden = true;
      document.body.appendChild(holder);
    }
    return holder;
  }

  // Public action handlers (quantity)
  /**
   * 追従バーの「−」。ページ内 quantity-selector の minus を代理クリックする。
   */
  handleQuantityDecrease = () => {
    this.#clickPageQuantityButton('minus');
  };

  /**
   * 追従バーの「＋」。ページ内 quantity-selector の plus を代理クリックする。
   */
  handleQuantityIncrease = () => {
    this.#clickPageQuantityButton('plus');
  };

  /**
   * @param {'minus' | 'plus'} name
   */
  #clickPageQuantityButton(name) {
    const productForm = this.#getProductForm();
    const button = /** @type {HTMLButtonElement | null} */ (
      productForm?.querySelector(`quantity-selector-component button[name="${name}"]`) ?? null
    );
    button?.click();
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    const productForm = this.#getProductForm();

    if (!productForm) return;
    if (!validateVariantSelection(productForm)) return;

    const targetButton = this.#resolveTargetAddToCartButton();

    if (!targetButton || targetButton.disabled) return;

    targetButton.dataset.puppet = 'true';

    targetButton.click();

    // #267: 了承モーダルが割り込む商品はこの時点ではカートに入らない。
    // チェックマークと fly-to-cart を出すと投入済みに見えてしまうので出さない。
    if (this.#hasConsentModal()) return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // #252: 外部（Swym / BIS スニペット）由来のノードは morph 対象 HTML に含まれないため、
    // そのまま morph すると破棄されてしまう。退避してから morph し、あとで戻す。
    const preserved = this.#detachExternalControls();

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.#reattachExternalControls(preserved);

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // #267: 了承モーダルが割り込む商品は、クリック時点ではまだカートに入らない。
    // フラグを戻すと次のクリックで fly-to-cart とチェックマークが復活してしまうので立てたままにする
    if (this.#hasConsentModal()) return;

    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    // #252: 常時表示モードでは隠さない
    if (this.dataset.alwaysVisible === 'true') return;

    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * 代理クリック先（ページ内の「カートに入れる」）を取得する。
   *
   * #266: 常時表示モードでは `#setupIntersectionObserver` を通らないため、以前は
   * バリエーションを切り替えるまで `#targetAddToCartButton` が null で、初回クリックが
   * 無反応（カートに入らない）だった。morph でボタンが差し替わることもあるので、
   * 参照が DOM から外れていたら毎回引き直す。
   * @returns {HTMLButtonElement | null}
   */
  #resolveTargetAddToCartButton() {
    const cached = this.#targetAddToCartButton;
    if (cached?.isConnected) return cached;

    const productForm = this.#getProductForm();
    this.#targetAddToCartButton = /** @type {HTMLButtonElement | null} */ (
      productForm?.querySelector('[ref="addToCartButton"]') ?? null
    );

    return this.#targetAddToCartButton;
  }

  /**
   * この商品に了承モーダルが付いているか（付いていればカート投入は即時ではない）
   * @returns {boolean}
   */
  #hasConsentModal() {
    const productId = this.dataset.productId;
    if (!productId) return false;

    const section = this.closest('.shopify-section');
    return section?.querySelector(`consent-modal-component[data-product-id="${productId}"]`) != null;
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber, qtyValue } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // #252: バー内の個数表示も同期する
    if (qtyValue) qtyValue.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
